/**
 * Reading a participant's stored quota assignment against the quota read.
 * Nothing here evaluates a rule: the segments come from the response's
 * `quotaAssignment` (computed server-side when it entered the pool, FR-21) and
 * every fullness signal from the quota read's server-computed flags.
 */
import { t } from '@lingui/core/macro';

import type {
  QuotaRefusalReason,
  QuotaV2BucketDto,
  QuotaV2Dto,
  ScreenerResponse,
} from '@/api/core-api/types.gen';

import { NO_MATCH_SEGMENT_ID } from './quota-draft';

export interface AssignedBucket {
  groupId: string;
  groupName: string;
  segmentId: string;
  /** The segment's name, or "No match" for the implicit bucket. */
  segmentName: string;
  isNoMatch: boolean;
  bucket: QuotaV2BucketDto;
}

/**
 * The participant's bucket in each group, in the quota's group order. A group
 * the stored assignment does not cover (a quota saved after an edit the
 * re-bucket has not reached) is left out rather than guessed.
 */
export function resolveAssignment(
  applicant: Pick<ScreenerResponse, 'quotaAssignment'>,
  quota: Pick<QuotaV2Dto, 'groups'> | null | undefined,
): AssignedBucket[] {
  const refs = applicant.quotaAssignment?.segments ?? [];
  if (!quota || refs.length === 0) return [];
  const byGroup = new Map(refs.map((ref) => [ref.groupId, ref.segmentId]));
  return quota.groups.flatMap((group): AssignedBucket[] => {
    const segmentId = byGroup.get(group.id);
    if (!segmentId) return [];
    if (segmentId === NO_MATCH_SEGMENT_ID) {
      return [
        {
          groupId: group.id,
          groupName: group.name,
          segmentId,
          segmentName: t`No match`,
          isNoMatch: true,
          bucket: group.noMatch,
        },
      ];
    }
    const segment = group.segments.find(
      (candidate) => candidate.id === segmentId,
    );
    return segment
      ? [
          {
            groupId: group.id,
            groupName: group.name,
            segmentId,
            segmentName: segment.name,
            isNoMatch: false,
            bucket: segment,
          },
        ]
      : [];
  });
}

/**
 * Whether the participant counts against their segments now, or would if
 * invited: `counted` for invited and attended, `released` once a no-show or
 * cancellation gave the place back, `pending` while in the pool.
 */
export type QuotaStanding = 'pending' | 'counted' | 'released';

export function quotaStanding(
  applicant: Pick<ScreenerResponse, 'quotaAssignment'>,
): QuotaStanding | null {
  switch (applicant.quotaAssignment?.tier) {
    case 'PENDING':
      return 'pending';
    case 'INVITED':
    case 'ATTENDED':
      return 'counted';
    case 'RELEASED':
      return 'released';
    default:
      return null;
  }
}

/** Why the invite gate would refuse a landing in this bucket right now, if it would. */
export function refusalReasonFor(
  bucket: QuotaV2BucketDto,
): QuotaRefusalReason | null {
  if (bucket.closed) return 'SEGMENT_FULL';
  if (bucket.minProtected) return 'MIN_PROTECTION';
  return null;
}

/** The participant's buckets that would refuse an invite right now. */
function blockingBuckets(
  applicant: Pick<ScreenerResponse, 'quotaAssignment'>,
  quota: Pick<QuotaV2Dto, 'groups'> | null | undefined,
): AssignedBucket[] {
  return resolveAssignment(applicant, quota).filter(
    (assigned) => refusalReasonFor(assigned.bucket) !== null,
  );
}

/**
 * The refusal reasons inviting these participants would meet, from the
 * current reading. The invite dialog shows them before anything is sent and
 * sends the researcher's acknowledgement with the invite; the gate stays the
 * authority and names anything this missed.
 */
export function predictedRefusals(
  applicants: ReadonlyArray<Pick<ScreenerResponse, 'id' | 'quotaAssignment'>>,
  quota: Pick<QuotaV2Dto, 'groups'> | null | undefined,
): {
  reasons: Set<QuotaRefusalReason>;
  byApplicant: Map<string, AssignedBucket[]>;
} {
  const reasons = new Set<QuotaRefusalReason>();
  const byApplicant = new Map<string, AssignedBucket[]>();
  for (const applicant of applicants) {
    if (quotaStanding(applicant) === 'counted') continue;
    const blocking = blockingBuckets(applicant, quota);
    if (blocking.length === 0) continue;
    byApplicant.set(applicant.id, blocking);
    for (const assigned of blocking) {
      const reason = refusalReasonFor(assigned.bucket);
      if (reason) reasons.add(reason);
    }
  }
  return { reasons, byApplicant };
}

/**
 * One `quotaSegments` filter value for the applicant list: the ids
 * percent-encoded and joined by `:` (partner ids may contain the separator).
 */
export function encodeSegmentFilter(
  groupId: string,
  segmentId: string,
): string {
  return `${encodeURIComponent(groupId)}:${encodeURIComponent(segmentId)}`;
}

/** Whether a value has `encodeSegmentFilter`'s shape; core-api checks the same pattern. */
export function isEncodedSegmentFilter(value: string): boolean {
  return /^[^:]+:[^:]+$/.test(value);
}
